// @flow

import React, { Component } from 'react';
import classnames from 'classnames';
import { NavLink, Redirect } from 'react-router-dom';
import type { Props } from '.';
import LoadingModal from '../../modals/loading';
import PageHeader from '../../page-header';
import Pagination from '../../pagination';
import iconAlert from '../../../images/icons/icon_alert.svg';
import TermsAndConditionsModal from '../../modals/termsandconditions';
import '../work.scss';

class SearchResults extends Component<Props> {
  props: Props;

  constructor(...args) {
    super(...args);
    this.handlePreviousPage = this.handlePreviousPage.bind(this);
    this.handleNextPage = this.handleNextPage.bind(this);
  }

  componentDidMount() {
    const { getWorks, searchParams, sodtncAccepted } = this.props;
    if (sodtncAccepted) {
      getWorks(searchParams);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      user,
      searchParams,
      getWorks,
      sodtncAccepted,
    } = this.props;

    if (
      user &&
      user.isSodUser &&
      prevProps.user &&
      (user.cae !== prevProps.user.cae ||
        prevProps.searchParams.pageNumber !==
        searchParams.pageNumber ||
        sodtncAccepted !== prevProps.sodtncAccepted)
    ) {
      getWorks(searchParams);
    }
  }

  getTotalPages() {
    const { searchParams, count } = this.props;
    const { pageLength } = searchParams;
    return count / pageLength;
  }

  formatTitle(titles) {
    if (titles.length > 0) {
      return titles[0]
        .toLowerCase()
        .replace(/((^|[\s(])\w)/g,
          v => v.toUpperCase());
    }
    return '';
  }

  /**
   * This function transforms an array of writers which have last name first such as:
   * ["LAST-NAME, FIRST-NAME","LAST-NAME, FIRST-NAME","LAST-NAME, FIRST-NAME"]
   * and changes it into a string that has the first name first such as:
   * "First-name Last-name, First-name Last-name, First-name Last-name"
   * This function will also change the names from being uppercase to title case.
   * @param {[string]} writers
   */
  formattedWriters(writers) {
    if (writers.length > 0) {
      return writers
        .map(writer => writer
          // reverse the order of names
          .split(',')
          .reverse()
          .map(v => v.trim())
          .join(' '))
        .filter(x => x !== '')
        .join(', ')
        // Change to title case
        .toLowerCase()
        .replace(/((^|[\s(])\w)/g, v => v.toUpperCase());
    }
    return '';
  }

  handlePreviousPage() {
    const { updateSearchParam, searchParams, getWorks } = this.props;
    const { pageNumber } = searchParams;
    if (pageNumber > 0) {
      updateSearchParam('pageNumber', pageNumber - 1);
      getWorks();
    }
  }

  handleNextPage() {
    const { updateSearchParam, searchParams, getWorks } = this.props;
    const { pageNumber } = searchParams;
    const totalPages = this.getTotalPages();
    if (pageNumber < totalPages) {
      updateSearchParam('pageNumber', pageNumber + 1);
      getWorks();
    }
  }

  openWork(workKey) {
    const { updateSearchParam, resetWorkTabs } = this.props;
    resetWorkTabs();
    updateSearchParam('workNumber', workKey);
    updateSearchParam('workNumberType', 'ICE');
  }

  renderNoWorksOfMine() {
    return (
      <div className="my-works-no-results">
        <div className="my-works-no-results--error-msg">
          <img alt="alert icon" className="bg-alert-icon" src={iconAlert} />
          <p>It looks like you have no works registered with PRS for Music.</p>
        </div>
        <div className="my-works-no-results--link">
          <a href="https://www.prsformusic.com/works/registering-works" target="_blank" rel="noopener noreferrer" className="">
            How to register works
            <span className="prs-icon prs-icon-register" />
          </a>
        </div>
      </div>
    );
  }

  renderNoWorksDefault() {
    const { searchParams } = this.props;
    const { firstName, lastName, workTitle } = searchParams;
    let searchSubtitle = [workTitle, lastName, firstName]
      .filter(v => v !== '')
      .join(' + ');
    searchSubtitle = `'${searchSubtitle}'`;
    return (
      <div className="search-works-no-results">
        <h4>
          {'Sorry, we couldn\'t find any results for '}
          {searchSubtitle}
        </h4>
        <hr />
        <h3>Search tips</h3>
        <ul>
          <li>Check your spelling</li>
          <li>Try again using alternative keywords</li>
          <li>Broaden your search</li>
        </ul>
        <NavLink to="/search" className="btn btn-prs btn-outline-pigdeon btn-block btn-sm">Refine search</NavLink>
      </div>
    );
  }

  renderNoWorks() {
    const { count, useMyWorksNoResults } = this.props;
    if (count === 0) {
      if (useMyWorksNoResults) {
        return this.renderNoWorksOfMine();
      }
      return this.renderNoWorksDefault();
    }
    return null;
  }

  renderWorksCountMoreThan1000() {
    return (
      <h4 id="countMoreThan1000">
        <span className="smaller">Displaying first&nbsp;</span>
        1000
        <span className="smaller">&nbsp;results</span>
      </h4>
    );
  }

  renderWorksCountLessThan1000(count) {
    return (
      <h4 id="countLessThan10000">
        {count}
        {' '}
        <span className="smaller">results</span>
      </h4>
    );
  }

  renderWorksCount() {
    const { count } = this.props;
    if (count > 0) {
      if (count < 1000) {
        return this.renderWorksCountLessThan1000(count);
      }
      return this.renderWorksCountMoreThan1000();
    }
    return null;
  }

  renderContent() {
    const {
      title, subTitle, className, search, count, membershipNo, backLinkTo, backLinkText, linkTo,
      headerIcon, searchParams, backLinkOnClick, sodtncAccepted,
    } = this.props;
    const { pageNumber, pageLength } = searchParams;

    return (
      <section className={classnames(
        'main-content',
        'd-flex',
        'flex-column',
        className,
      )}
      >
        {sodtncAccepted !== true && <TermsAndConditionsModal />}
        <PageHeader
          title={title}
          subTitle={subTitle}
          iconClassName={headerIcon}
          membershipNo={membershipNo}
          backLinkTo={backLinkTo}
          backLinkText={backLinkText}
          backLinkOnClick={backLinkOnClick}
        />
        <div className="search-pane flex-grow-1">
          <div className="container">
            {this.renderNoWorks()}
            {this.renderWorksCount()}
            {count > 0 && (
              <React.Fragment>
                <div className="search-card-list">
                  {search.map(({ titles, writers, workKey }) => (
                    <NavLink className="search-card" to={linkTo} key={workKey} onClick={() => this.openWork(workKey)}>
                      <h5>{this.formatTitle(titles)}</h5>
                      <p>{this.formattedWriters(writers)}</p>
                    </NavLink>
                  ))}
                </div>
                <hr />
                <Pagination
                  handleNextPage={this.handleNextPage}
                  handlePreviousPage={this.handlePreviousPage}
                  count={count}
                  pageLength={pageLength}
                  pageNumber={pageNumber}
                />
              </React.Fragment>
            )}
          </div>
        </div>
      </section>
    );
  }

  render() {
    const { user, authenticating, fetching } = this.props;

    if (!user.isSodUser) {
      return <Redirect to="/" />;
    }

    return user && !authenticating && !fetching ?
      this.renderContent() :
      <LoadingModal />;
  }
}

export default SearchResults;
